import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState, type FormEvent, type MouseEvent } from 'react'
import { api } from '../../api/client'
import { useItem } from '../../api/queries'
import type { EquipmentDocument, EquipmentDocumentKind } from '../../api/types'
import { useUser } from '../../auth/AuthContext'
import { Icon } from '../../components/Icon'
import { Card, ErrorState, Field, LoadingState } from '../../components/ui'
import { can } from '../../lib/permissions'

const KINDS: [EquipmentDocumentKind, string][] = [['photo', 'Foto'], ['drawing', 'Plano'], ['datasheet', 'Ficha técnica'], ['other', 'Otro']]

const isImage = (d: EquipmentDocument) => d.content_type.startsWith('image/')
const label = (d: EquipmentDocument) => d.title || d.original_name || 'Archivo'
const thumbnailUrl = (d: EquipmentDocument) => `${d.url}?variant=thumb`

function UploadForm({ path }: { path: string }) {
  const client = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [kind, setKind] = useState<EquipmentDocumentKind>('photo')
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!file) return
    const formElement = e.currentTarget
    setBusy(true); setError(null)
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('kind', kind)
      form.append('title', title.trim())
      await api.upload<EquipmentDocument>(path, form)
      setFile(null); setTitle('')
      formElement.reset()
      await client.invalidateQueries({ predicate: (q) => q.queryKey[0] === path })
    } catch (err) { setError(err instanceof Error ? err.message : 'La carga falló.') } finally { setBusy(false) }
  }
  return (
    <form className="docs__upload" onSubmit={submit}>
      <Field label="Archivo" htmlFor="doc-file" hint="JPEG, PNG, WebP o PDF."><input id="doc-file" type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></Field>
      <Field label="Tipo" htmlFor="doc-kind"><select id="doc-kind" value={kind} onChange={(e) => setKind(e.target.value as EquipmentDocumentKind)}>{KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
      <Field label="Título (opcional)" htmlFor="doc-title"><input id="doc-title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} /></Field>
      <div className="row"><button type="submit" className="btn btn--primary btn--sm" disabled={!file || busy}><Icon name="upload" size={14} /> {busy ? 'Subiendo…' : 'Subir archivo'}</button></div>
      {error ? <p role="alert" className="form-error">{error}</p> : null}
    </form>
  )
}

/** Full-size viewer over the page: one image at a time, previous/next (buttons and arrow keys), Escape closes. */
function Lightbox({ items, index, onIndex, onClose }: { items: EquipmentDocument[]; index: number; onIndex: (i: number) => void; onClose: () => void }) {
  const titleId = useId()
  const closeRef = useRef<HTMLButtonElement>(null)
  const d = items[index]
  const many = items.length > 1
  const go = (step: number) => onIndex((index + step + items.length) % items.length)
  // Latest handlers for the one document-level listener, so re-renders (e.g. a background refetch) never re-run it.
  const keys = useRef({ go, onClose, many })
  keys.current = { go, onClose, many }
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      const { go: step, onClose: close, many: browse } = keys.current
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowRight' && browse) step(1)
      else if (e.key === 'ArrowLeft' && browse) step(-1)
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
      opener?.focus()
    }
  }, [])
  return (
    <div className="lightbox" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="lightbox__frame" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="lightbox__bar">
          <strong id={titleId}>{label(d)}</strong>
          {many ? <span className="lightbox__count">{index + 1} de {items.length}</span> : null}
          <a className="btn btn--ghost btn--sm" href={d.url} target="_blank" rel="noopener noreferrer"><Icon name="download" size={14} /> Abrir original</a>
          <button ref={closeRef} type="button" className="icon-btn" aria-label="Cerrar" onClick={onClose}><Icon name="x" size={18} /></button>
        </header>
        <div className="lightbox__stage" data-kind={d.kind}>
          <img src={d.url} alt={label(d)} />
          {many ? <button type="button" className="lightbox__nav lightbox__nav--prev" aria-label="Anterior" onClick={() => go(-1)}><Icon name="chevron-left" size={28} /></button> : null}
          {many ? <button type="button" className="lightbox__nav lightbox__nav--next" aria-label="Siguiente" onClick={() => go(1)}><Icon name="chevron-right" size={28} /></button> : null}
        </div>
      </div>
    </div>
  )
}

/** Photos, engineering drawings and other files of one equipment; staff can attach more. */
export function EquipmentDocuments({ equipmentId }: { equipmentId: number }) {
  const user = useUser()
  const path = `/api/equipment/${equipmentId}/documents/`
  const docs = useItem<EquipmentDocument[]>(path)
  const all = (Array.isArray(docs.data) ? docs.data : []).filter((d) => d.kind !== 'manual' && d.kind !== 'exploded')
  const images = [...all.filter((d) => d.kind === 'photo' && isImage(d)), ...all.filter((d) => d.kind !== 'photo' && isImage(d))]
  const others = all.filter((d) => !isImage(d))
  // Keyed by document id: a refetch that reorders the gallery keeps the viewer on the same document.
  const [openId, setOpenId] = useState<number | null>(null)
  const openIndex = images.findIndex((d) => d.id === openId)
  // A plain click opens the viewer; ctrl/cmd/middle click keep the link's new-tab behavior.
  const show = (e: MouseEvent<HTMLAnchorElement>, i: number) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    setOpenId(images[i].id)
  }
  return (
    <Card title="Fotos y planos">
      {docs.isPending ? <LoadingState label="Cargando fotos y planos…" /> : docs.isError ? <ErrorState error={docs.error} onRetry={() => void docs.refetch()} /> : all.length === 0 ? <p className="muted">Sin fotos ni planos</p> : (
        <div className="docs">
          {images.length ? (
            <ul className="gallery" aria-label="Galería">
              {images.map((d, i) => (
                <li key={d.id} className="gallery__item" data-kind={d.kind}>
                  <a href={d.url} target="_blank" rel="noopener noreferrer" aria-label={`Ver ${label(d)}`} onClick={(e) => show(e, i)}>
                    <img src={thumbnailUrl(d)} alt="" loading="lazy" decoding="async" />
                  </a>
                  <span className="gallery__caption">{label(d)}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {others.length ? (
            <ul className="plain" aria-label="Otros archivos">
              {others.map((d) => <li key={d.id}><a href={d.url} target="_blank" rel="noopener noreferrer"><Icon name="download" size={14} /> {label(d)}</a></li>)}
            </ul>
          ) : null}
        </div>
      )}
      {can(user.role, 'equipment.documents') ? <UploadForm path={path} /> : null}
      {openIndex >= 0 ? <Lightbox items={images} index={openIndex} onIndex={(i) => setOpenId(images[i].id)} onClose={() => setOpenId(null)} /> : null}
    </Card>
  )
}
